import { supabase } from './supabase';

/**
 * ログアウト管理クラス
 * 安全で確実なログアウト処理を提供
 */
export class LogoutManager {
  private static readonly LOGOUT_CONFIRM_MESSAGE =
    'ログアウトが完了しました。指定されたページに移動しますか？';
  private static readonly SUPABASE_AUTH_PREFIX = 'supabase.auth.';
  private static readonly AUTH_PREFIX = 'auth.';
  private static readonly TOKEN_KEY = 'token';
  private static readonly SESSION_KEY = 'session';
  private static readonly USER_KEY = 'user';
  private static isHandlingSessionExpiry = false; // セッション期限切れ処理中フラグ

  /**
   * ローカルストレージとセッションストレージをクリア
   */
  private static clearLocalStorage(): void {
    if (typeof window !== 'undefined') {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(this.SUPABASE_AUTH_PREFIX) || key.startsWith(this.AUTH_PREFIX)) {
          localStorage.removeItem(key);
        }
      });

      // セッションストレージもクリア
      const sessionKeys = Object.keys(sessionStorage);
      sessionKeys.forEach(key => {
        if (key.startsWith(this.SUPABASE_AUTH_PREFIX) || key.startsWith(this.AUTH_PREFIX)) {
          sessionStorage.removeItem(key);
        }
      });
    }
  }

  /**
   * リダイレクト処理
   */
  private static handleRedirect(redirectTo: string, showConfirm: boolean): void {
    if (typeof window !== 'undefined') {
      // 確認ダイアログを表示（オプション）
      if (showConfirm) {
        // eslint-disable-next-line no-alert
        const confirmed = window.confirm(LogoutManager.LOGOUT_CONFIRM_MESSAGE);
        if (confirmed) {
          window.location.href = redirectTo;
        } else {
          // 確認がキャンセルされた場合もTOPページに移動
          window.location.href = '/';
        }
      } else {
        window.location.href = redirectTo;
      }
    }
  }

  /**
   * 完全ログアウトを実行
   */
  static async performCompleteLogout(
    redirectTo?: string,
    showConfirm: boolean = true
  ): Promise<void> {
    try {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.log('Performing complete logout');
      }

      // 1. Supabaseからログアウト
      if (!supabase) {
        console.warn('Supabase client is not initialized, skipping Supabase logout');
        return;
      }

      const { error } = await supabase.auth.signOut();

      if (error) {
        console.error('Error signing out from Supabase:', error);
        // エラーが発生しても続行
      }

      // 2. ローカルストレージから認証データをクリア
      this.clearLocalStorage();

      // 3. ログアウト完了の確認
      const isLoggedOut = await this.verifyLogoutComplete();

      if (!isLoggedOut) {
        console.warn('Logout verification failed - some auth data still present');
        // 最後の手段としてページリロード
        if (typeof window !== 'undefined') {
          window.location.reload();
          return;
        }
      }

      // 4. リダイレクト（デフォルトはTOPページ）
      const finalRedirectTo = redirectTo || '/';
      this.handleRedirect(finalRedirectTo, showConfirm);

      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.log('Complete logout successful');
      }
    } catch (error) {
      console.error('Error during complete logout:', error);
      // エラーが発生した場合は緊急ログアウトを実行
      await this.emergencyLogout('Complete logout failed');
    }
  }

  /**
   * 緊急ログアウトを実行
   * 通常のログアウトが失敗した場合の最後の手段
   */
  static async emergencyLogout(reason?: string): Promise<void> {
    try {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.error('Emergency logout triggered:', reason);
      }

      // 1. ローカルストレージを強制的にクリア
      if (typeof window !== 'undefined') {
        // 認証関連のデータをすべてクリア
        const keys = Object.keys(localStorage);
        keys.forEach(key => {
          if (
            key.startsWith(this.SUPABASE_AUTH_PREFIX) ||
            key.startsWith(this.AUTH_PREFIX) ||
            key.includes(this.TOKEN_KEY) ||
            key.includes(this.SESSION_KEY) ||
            key.includes(this.USER_KEY)
          ) {
            localStorage.removeItem(key);
          }
        });

        // セッションストレージもクリア
        sessionStorage.clear();
      }

      // 2. Supabaseのセッションを強制的にクリア
      try {
        if (supabase) {
          await supabase.auth.signOut();
        }
      } catch (signOutError) {
        console.error('Error during emergency signOut:', signOutError);
        // エラーが発生しても続行
      }

      // 3. 即座にホームページにリダイレクト
      if (typeof window !== 'undefined') {
        window.location.href = '/';
      }
    } catch (error) {
      console.error('Error during emergency logout:', error);
      // 最後の手段: ページリロード
      if (typeof window !== 'undefined') {
        window.location.reload();
      }
    }
  }

  /**
   * ログアウト確認ダイアログ
   */
  static async confirmLogout(
    message: string = 'ログアウトしますか？',
    redirectTo?: string
  ): Promise<boolean> {
    try {
      if (typeof window === 'undefined') return true;

      // eslint-disable-next-line no-alert
      const confirmed = window.confirm(message);

      if (confirmed) {
        await this.performCompleteLogout(redirectTo);
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error in logout confirmation:', error);
      return false;
    }
  }

  /**
   * セッション期限切れ時の自動ログアウト
   */
  static async handleSessionExpiry(): Promise<void> {
    // 既に処理中の場合は何もしない
    if (this.isHandlingSessionExpiry) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.log('Session expiry handling already in progress, skipping...');
      }
      return;
    }

    // グローバルフラグもチェック（ページレベルでの重複実行防止）
    if (typeof window !== 'undefined' && (window as any).__sessionExpiryHandling) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.log('Session expiry handling already in progress globally, skipping...');
      }
      return;
    }

    this.isHandlingSessionExpiry = true;
    if (typeof window !== 'undefined') {
      (window as any).__sessionExpiryHandling = true;
    }

    try {
      // eslint-disable-next-line no-console
      console.log('Session expired, performing automatic logout');

      // ユーザーに通知（オプション）
      if (typeof window !== 'undefined' && window.confirm) {
        // eslint-disable-next-line no-alert
        const shouldRedirect = window.confirm(
          'セッションが期限切れになりました。ホームページに戻りますか？'
        );

        const redirectTo = shouldRedirect ? '/' : window.location.pathname;
        await this.performCompleteLogout(redirectTo, false);
      } else {
        await this.performCompleteLogout('/', false);
      }
    } catch (error) {
      console.error('Error handling session expiry:', error);
      await this.emergencyLogout('Session expiry handling failed');
    } finally {
      // 処理完了後にフラグをリセット
      this.isHandlingSessionExpiry = false;
      if (typeof window !== 'undefined') {
        (window as any).__sessionExpiryHandling = false;
      }
    }
  }

  /**
   * ログアウト状態の検証
   */
  static async verifyLogoutComplete(): Promise<boolean> {
    try {
      // セッションが完全にクリアされているかチェック
      const session = await this.getCurrentSession();
      const isTokenValid = await this.isTokenValid();

      // ローカルストレージの認証データもチェック
      let hasLocalAuthData = false;
      if (typeof window !== 'undefined') {
        const keys = Object.keys(localStorage);
        hasLocalAuthData = keys.some(key => key.startsWith('supabase.auth.'));
      }

      const isLoggedOut = !session && !isTokenValid && !hasLocalAuthData;

      if (!isLoggedOut) {
        console.warn('Logout verification failed - some auth data still present');
      }

      return isLoggedOut;
    } catch (error) {
      console.error('Error verifying logout:', error);
      return false;
    }
  }

  /**
   * 現在のセッション情報を取得
   */
  private static async getCurrentSession() {
    try {
      const {
        data: { session },
        error,
      } = supabase ? await supabase.auth.getSession() : { data: { session: null }, error: null };

      if (error) {
        console.error('Error getting session:', error);
        return null;
      }
      return session;
    } catch (error) {
      console.error('Error getting current session:', error);
      return null;
    }
  }

  /**
   * トークンの有効期限をチェック
   */
  private static async isTokenValid(): Promise<boolean> {
    try {
      const session = await this.getCurrentSession();

      if (!session || !session.access_token) {
        return false;
      }

      // トークンの有効期限をチェック
      const expiresAt = session.expires_at;
      if (!expiresAt) {
        return false;
      }

      const now = Math.floor(Date.now() / 1000);
      return expiresAt > now;
    } catch (error) {
      console.error('Error checking token validity:', error);
      return false;
    }
  }
}

// 便利な関数をエクスポート
export const performLogout = LogoutManager.performCompleteLogout;
export const emergencyLogout = LogoutManager.emergencyLogout;
export const confirmLogout = LogoutManager.confirmLogout;
